import React from 'react';
import Grid from "@material-ui/core/Grid";
import Chip from '@material-ui/core/Chip';
import { formatDateOnly } from '../../../common/functions';
import { useGlobalState } from '../../../state';
import MaterialTypeChips from './materialTypeChips';
import { minChipLabelMobile } from '../../../views/FeedFormulation/utils/constants';
import PPMobileCardView from '../../../components/PPMobileCardView';
import { AutoGeneratedChip } from './autoGenerated';
import { getPurchasePaymentStatus } from '../utils/paymentStatus';

// Same states as the desktop Payment Status icons
const STATUS_PILL = {
    notPaid: { label: 'Not Paid', backgroundColor: '#f44336', color: '#fff' },
    partial: { label: 'Partially Paid', backgroundColor: '#ffeb3b', color: '#000' },
    paid: { label: 'Paid', backgroundColor: '#4caf50', color: '#fff' },
};
const statusPill = (status, prefix = '') => (
    <Chip
        label={prefix + STATUS_PILL[status].label}
        style={{ backgroundColor: STATUS_PILL[status].backgroundColor, color: STATUS_PILL[status].color }}
        size="small"
        className="pp-pill"
    />
);

const Mobile = ({data, materialTypes, viewDetails, onDelete, onEdit, onPaymentEdit, onGenerateInvoice, isProPlusVersion, amountObj}) => {
    const [{user}] = useGlobalState();

    function calculateTotalAmount (rowData) {
        if (isProPlusVersion) {
            let totalAmount = 0
            let gstAmount = 0
            let tdsAmount = 0
            let tcsAmount = 0
            let billValue = 0
            if (rowData && rowData.purchaseMaterials) {
                let purchaseMaterials = rowData.purchaseMaterials
                purchaseMaterials.forEach(purchaseMaterial => {
                    let gstTaxAmt = parseFloat((purchaseMaterial.billValue * (purchaseMaterial.gst ? purchaseMaterial.gst : 0) / 100).toFixed(2))
                    let tdsTaxAmt = parseFloat((purchaseMaterial.billValue * (rowData.tds ? rowData.tds : 0) / 100).toFixed(2))
                    let tcsTaxAmt = parseFloat((purchaseMaterial.billValue * (rowData.tcs ? rowData.tcs : 0) / 100).toFixed(2))
                    gstAmount += gstTaxAmt
                    tdsAmount += tdsTaxAmt
                    tcsAmount += tcsTaxAmt
                    billValue += purchaseMaterial.billValue
                })
            }
            let gstTCSVal = gstAmount * (rowData.tcs/100)
            let totalTCS = Math.ceil(tcsAmount + gstTCSVal)
            totalAmount = billValue + totalTCS + gstAmount - tdsAmount
            return totalAmount.toLocaleString(user.locales,amountObj)
        } else { // pro version
            return (rowData.billValue).toLocaleString(user.locales,amountObj)
        }
    }

    function calculateRemainingBalance(rowData) {
        let totalAmount = 0
        let gstAmount = 0
        let tdsAmount = 0
        let tcsAmount = 0
        let billValue = 0
        let totalAmountPaid = 0
        if (rowData && rowData.purchaseMaterials) {
            let purchaseMaterials = rowData.purchaseMaterials
            purchaseMaterials.forEach(purchaseMaterial => {
                let gstTaxAmt = parseFloat((purchaseMaterial.billValue * (purchaseMaterial.gst ? purchaseMaterial.gst : 0) / 100).toFixed(2))
                let tdsTaxAmt = parseFloat((purchaseMaterial.billValue * (rowData.tds ? rowData.tds : 0) / 100).toFixed(2))
                let tcsTaxAmt = parseFloat((purchaseMaterial.billValue * (rowData.tcs ? rowData.tcs : 0) / 100).toFixed(2))
                gstAmount += gstTaxAmt
                tdsAmount += tdsTaxAmt
                tcsAmount += tcsTaxAmt
                billValue += purchaseMaterial.billValue
            })
        }
        if (rowData && rowData.purchasePayments && rowData.purchasePayments.length) {
            let purchasePayments = rowData.purchasePayments
            purchasePayments.forEach(ele => {
                if (ele.paymentMode !== -1 ) { // Exclude tax payment
                    totalAmountPaid += ele.paymentAmount
                }
            })
        }
        let gstTCSVal = gstAmount * (rowData.tcs/100)
        let totalTCS = Math.ceil(tcsAmount + gstTCSVal)
        totalAmount = billValue + totalTCS + gstAmount - tdsAmount
        
        let remainingAmount = totalAmount - totalAmountPaid;

        if (rowData.paymentStatus === 2 && remainingAmount < 1) {
            remainingAmount = 0
        }

        return remainingAmount.toLocaleString(user.locales,amountObj)
    }

    function calculateBillValue(rowData) {
        let billValue = 0
        if (rowData && rowData.purchaseMaterials) {
            let purchaseMaterials = rowData.purchaseMaterials
            purchaseMaterials.forEach(purchaseMaterial => {
                billValue += purchaseMaterial.billValue
            })
        }
        return billValue.toLocaleString(user.locales,amountObj)
    }

    // Same as the desktop Payment Status icons: one "Paid" when the payment and the tax (if any) are both
    // paid, otherwise the payment pill plus the tax pill so it's clear which part is still due
    const status = getPurchasePaymentStatus(data);
    const allPaid = status.payment === 'paid' && (status.tax === null || status.tax === 'paid');
    const paymentStatusPills = allPaid ? statusPill('paid') : (
        <span style={{ display: 'flex', gap: 4 }}>
            {statusPill(status.payment)}
            {status.tax && statusPill(status.tax, 'Tax ')}
        </span>
    );
    const rows = isProPlusVersion
        ? [
            { label: 'Customer', value: data.vendorName || 'N/A', fullWidth: true, hideLabel: true, bold: true, fontSize: 14 },
            { label: 'Total Amount', value: calculateTotalAmount(data) },
            { label: 'Bill Value', value: calculateBillValue(data) },
            { label: 'Remaining', value: calculateRemainingBalance(data) },
            { label: 'Payment', inHeader: true, hideLabel: true, value: paymentStatusPills },
        ]
        : [
            { label: 'Farm Name', value: data.farmName || 'N/A' },
            { label: 'Total Amount', value: calculateTotalAmount(data) },
        ];

    const menuItems = [
        viewDetails && { label: 'Details', onClick: viewDetails },
        onEdit && { label: 'Edit', onClick: onEdit },
        onPaymentEdit && { label: 'Payment', onClick: onPaymentEdit },
        onGenerateInvoice && { label: 'Generate Invoice', onClick: onGenerateInvoice },
        onDelete && { label: 'Delete', onClick: onDelete },
    ].filter(Boolean);

    return (
        <PPMobileCardView
            title={<span>{formatDateOnly(data.date, "DD/MM/YYYY")}<AutoGeneratedChip purchase={data}/></span>}
            id={data.billNumber || data.id}
            rows={rows}
            menuItems={menuItems}
            onClick={viewDetails}
        >
            {!isProPlusVersion && data?.purchaseMaterials?.length > 0 && (
                <div style={{ marginTop: 4 }}>
                    <div className="pp-card-label" style={{ marginBottom: 4 }}>Material Type</div>
                    <Grid container spacing={1} wrap='nowrap'>
                        <MaterialTypeChips purchaseMaterials={data?.purchaseMaterials} materialTypes={materialTypes} maxChips={minChipLabelMobile}/>
                    </Grid>
                </div>
            )}
        </PPMobileCardView>
    );
};

export default Mobile;